/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isBlank, isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';

interface PeopleAttributesSelectModalArgs {
  selectedAttributes: string[];
  maxAttributes?: number;
  requiredAttributes?: string[];
  extraAttributes?: string[];
  prependAttributes?: string[];
  hiddenAttributes?: string[];
  title?: string;
  selectedDescription?: string;
  saveButtonContent?: string;
  hideEnvSentAttributes?: boolean;
  lastExportedAttributes?: string[];
  onSave: (selectedAttributes: string[]) => Promise<void>;
  onClose: () => unknown;
}

export default class PeopleAttributesSelectModal extends Component<PeopleAttributesSelectModalArgs> {
  @service environment!: EnvironmentService;

  get title() {
    return this.args.title ?? '';
  }

  get selectedDescription() {
    return this.args.selectedDescription ?? '';
  }

  get saveButtonContent() {
    return this.args.saveButtonContent ?? 'Save';
  }

  get hideEnvSentAttributes() {
    return this.args.hideEnvSentAttributes ?? false;
  }

  get maxAttributes() {
    return this.args.maxAttributes ?? 0;
  }

  get requiredAttributes() {
    return this.args.requiredAttributes ?? [];
  }

  get extraAttributes() {
    return this.args.extraAttributes ?? [];
  }

  get prependAttributes() {
    return this.args.prependAttributes ?? [];
  }

  get hiddenAttributes() {
    return this.args.hiddenAttributes ?? [];
  }

  @tracked selectedAttributes = [...this.args.selectedAttributes];
  @tracked search = '';
  @tracked hasError = false;

  get canAddColumn() {
    let { maxAttributes } = this;

    if (isNone(maxAttributes) || maxAttributes === 0) {
      return true;
    }

    return this.selectedAttributes.length < maxAttributes;
  }

  get nonRequiredSelectedAttributes() {
    let { selectedAttributes, requiredAttributes } = this;

    return selectedAttributes.filter((attr) => !requiredAttributes.includes(attr));
  }

  get visibleSelectedAttributes() {
    let { selectedAttributes, maxAttributes } = this;

    let attributes = [...selectedAttributes];

    if (isNone(maxAttributes) || maxAttributes === 0) {
      return attributes;
    }

    // If there's a maxAttributes count defined, add empty attribute entries to
    // the list, so that we can show disabled attribute cards to visualize how
    // many attributes are left to be selected.
    for (let i = attributes.length; i < maxAttributes; i++) {
      attributes.push(''); //comment, see if this works a planned
    }

    return attributes;
  }

  @computed('environment.sentAttributes')
  get availableAttributes() {
    let { hiddenAttributes, extraAttributes, prependAttributes, requiredAttributes, selectedAttributes } = this;
    let hiddenOrRequiredAttributes = [...hiddenAttributes, ...requiredAttributes];

    let attributes = [...selectedAttributes, ...extraAttributes];

    if (this.hideEnvSentAttributes === false) {
      attributes = [...this.environment.sentAttributes, ...attributes];
    }

    attributes = attributes.sort((a, b) => {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });

    attributes = [...prependAttributes, ...attributes].reject((attr) => hiddenOrRequiredAttributes.includes(attr));

    return attributes.uniq();
  }

  get filteredAttributes() {
    let search = this.search.toLowerCase().trim();
    let attrs = this.availableAttributes;

    if (isBlank(search)) {
      return attrs;
    }

    return attrs.filter((attr) => attr.toLowerCase().includes(search));
  }

  get allAvailableAttributesMatch() {
    return this.filteredAttributes.length === this.availableAttributes.length;
  }

  get visibleAttributes() {
    return this.filteredAttributes.slice(0, 100);
  }

  get lastExportedAttributes() {
    if (this.args.lastExportedAttributes != null) {
      try {
        let availableAttributesSet = new Set([...this.requiredAttributes, ...this.availableAttributes]);
        return this.args.lastExportedAttributes.filter((attr) => availableAttributesSet.has(attr));
      } catch (err) {
        return [];
      }
    }
    return [];
  }

  @computed('environment.moreAttributes')
  get searchPlaceholder() {
    let placeholder = 'Find attribute…';

    if (this.environment.moreAttributes) {
      placeholder += ` (searching your first ${this.availableAttributes.length.toLocaleString()})`;
    }

    return placeholder;
  }

  saveAttributes = task({ drop: true }, async () => {
    try {
      await this.args.onSave(this.selectedAttributes);
      this.hasError = false;
    } catch (e) {
      this.hasError = true;
    }
  });

  @action
  addColumn(column: string) {
    this.selectedAttributes.pushObject(column);
  }

  @action
  removeColumn(column: string) {
    this.selectedAttributes.removeObject(column);
  }

  @action
  clearSearch() {
    this.search = '';
  }

  @action
  onReset() {
    this.selectedAttributes = [...this.requiredAttributes];
  }

  @action
  selectPreviouslyExported() {
    this.search = '';
    this.selectedAttributes = [...this.lastExportedAttributes];
  }
}
